import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import logger from '../lib/logger';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Loader2, Delete, User, Scissors, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const BARRABAS_LOGO = "https://customer-assets.emergentagent.com/job_771f31db-11b6-4883-8e50-35de394374ea/artifacts/xf4slwx6_LOGO%20VB.jpg.jpeg";

// Role labels in Spanish
const ROLE_LABELS = {
  master_admin: 'Administrador',
  admin: 'Administrador',
  barber: 'Barbero',
  stylist: 'Estilista',
  client: 'Cliente'
};

export default function LoginPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  const { loginWithPin } = useAuth();
  const navigate = useNavigate();

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const usersRes = await authAPI.getUsersForLogin();
      setUsers(usersRes.data || []);
    } catch (error) {
      logger.error('Error loading users:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Single business: every active staff profile is shown
  const filteredUsers = users;

  const handleUserClick = (user) => {
    // PIN-only access: profiles without a PIN must get one from the Master Admin.
    if (user.has_pin === false) {
      toast.error('Este perfil no tiene PIN asignado. Pide al Master Admin que te asigne uno.');
      return;
    }
    setSelectedUser(user);
    setPin('');
    setPinDialogOpen(true);
  };

  const handlePinDelete = useCallback(() => {
    setPin(prev => prev.slice(0, -1));
  }, []);

  const handlePinSubmit = useCallback(async (pinCode) => {
    if (!selectedUser || pinCode.length !== 4) return;
    
    setLoginLoading(true);
    try {
      // Use AuthContext's loginWithPin which handles state updates
      const result = await loginWithPin(selectedUser.id, pinCode);
      
      // Ensure axios headers are set before navigation
      if (result.access_token) {
        // Double-check headers are set (belt and suspenders approach)
        axios.defaults.headers.common['Authorization'] = `Bearer ${result.access_token}`;
        if (result.businesses?.length > 0) {
          axios.defaults.headers.common['X-Business-Id'] = result.businesses[0].id;
        }
      }
      
      toast.success(`Bienvenido, ${selectedUser.first_name}!`, { id: 'login-welcome' });
      setPinDialogOpen(false);
      
      // Small delay to ensure state propagation before navigation
      setTimeout(() => {
        navigate('/agenda', { replace: true });
      }, 100);
    } catch (error) {
      logger.error('PIN login error:', error);
      toast.error(error.response?.data?.detail || 'PIN incorrecto');
      setPin('');
      setLoginLoading(false);
    }
  }, [selectedUser, loginWithPin, navigate]);

  const handlePinInput = useCallback((digit) => {
    setPin(prev => {
      if (prev.length < 4) {
        const newPin = prev + digit;
        // Auto-submit when 4 digits entered
        if (newPin.length === 4) {
          // Use setTimeout to allow state update before submit
          setTimeout(() => handlePinSubmit(newPin), 50);
        }
        return newPin;
      }
      return prev;
    });
  }, [handlePinSubmit]);

  // Handle physical keyboard input for PIN (both main keyboard and numpad)
  useEffect(() => {
    if (!pinDialogOpen || loginLoading) return;

    const handleKeyDown = (e) => {
      // Prevent if typing in an input field (except in PIN dialog)
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      // Handle numeric keys from main keyboard (0-9) and numpad (Numpad0-Numpad9)
      const isMainKeyboardNumber = /^[0-9]$/.test(e.key);
      const isNumpadNumber = e.code && e.code.startsWith('Numpad') && /^[0-9]$/.test(e.key);
      
      if (isMainKeyboardNumber || isNumpadNumber) {
        e.preventDefault();
        handlePinInput(e.key);
      }
      // Handle Backspace/Delete from both keyboards
      else if (e.key === 'Backspace' || e.key === 'Delete' || e.code === 'NumpadDecimal') {
        e.preventDefault();
        handlePinDelete();
      }
      // Handle Escape to close dialog
      else if (e.key === 'Escape') {
        setPinDialogOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pinDialogOpen, loginLoading, handlePinInput, handlePinDelete]);

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  // Get current time in CDMX timezone
  const getCDMXTime = () => {
    return new Date().toLocaleString('es-MX', {
      timeZone: 'America/Mexico_City',
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const [currentTime, setCurrentTime] = useState(getCDMXTime());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(getCDMXTime());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex flex-col">
      {/* Header with business brand */}
      <header className="py-6 px-8">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3" data-testid="login-brand">
              <div className="w-14 h-14 rounded-xl overflow-hidden bg-white shadow-lg border border-amber-200">
                <img src={BARRABAS_LOGO} alt="Barrabás" className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                  Barrabás & JL Saloon
                </p>
                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                  <Scissors className="w-3 h-3" />
                  Barbería · Salón
                  <Sparkles className="w-3 h-3" />
                </div>
              </div>
            </div>
          </div>

          {/* Current time */}
          <div className="text-right">
            <p className="text-sm text-slate-500 capitalize">{currentTime}</p>
            <p className="text-xs text-slate-400">Ciudad de México (GMT-6)</p>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-8 pb-12">
        <div className="w-full max-w-4xl">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope' }}>
              Selecciona tu perfil
            </h1>
            <p className="text-slate-500">
              Toca tu nombre para iniciar sesión con tu código PIN
            </p>
          </div>

          {/* Users Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-8" data-testid="login-profiles-grid">
            {filteredUsers.length === 0 && (
              <div className="col-span-full text-center text-slate-400 text-sm py-8">
                No hay perfiles disponibles para este negocio.
              </div>
            )}
            {filteredUsers.map((user) => (
              <button
                key={user.id}
                onClick={() => handleUserClick(user)}
                className="group bg-white rounded-2xl p-6 shadow-sm border border-slate-200 hover:shadow-lg hover:border-primary/30 transition-all duration-200 flex flex-col items-center gap-3"
                data-testid={`user-card-${user.id}`}
              >
                <Avatar className="w-20 h-20 border-4 border-slate-100 group-hover:border-primary/20 transition-colors shadow-md">
                  <AvatarImage src={user.avatar_url} />
                  <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/10 text-primary text-xl font-semibold">
                    {getInitials(user.first_name, user.last_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="text-center">
                  <p className="font-semibold text-slate-800 group-hover:text-primary transition-colors">
                    {user.first_name}
                  </p>
                  <p className="text-sm text-slate-500">
                    {user.last_name}
                  </p>
                  <span className={cn(
                    "inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-medium",
                    user.role === 'master_admin' || user.role === 'admin' 
                      ? "bg-purple-100 text-purple-700" 
                      : user.role === 'barber' || user.role === 'stylist'
                      ? "bg-blue-100 text-blue-700"
                      : "bg-slate-100 text-slate-600"
                  )}>
                    {ROLE_LABELS[user.role] || user.role}
                  </span>
                  {user.has_pin === false && (
                    <p className="mt-1 text-[10px] text-slate-400">
                      Sin PIN · pídelo al Master Admin
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center">
        <p className="text-xs text-slate-400">
          © Barrabás & JL Saloon
        </p>
      </footer>

      {/* PIN Dialog */}
      <Dialog open={pinDialogOpen} onOpenChange={setPinDialogOpen}>
        <DialogContent className="max-w-sm" aria-describedby="pin-dialog-description">
          <DialogHeader>
            <DialogTitle className="text-center" style={{ fontFamily: 'Manrope' }}>
              Ingresa tu código PIN
            </DialogTitle>
            <p id="pin-dialog-description" className="sr-only">
              Ingresa tu código PIN de 4 dígitos para iniciar sesión
            </p>
          </DialogHeader>

          {selectedUser && (
            <div className="flex flex-col items-center py-4">
              <Avatar className="w-20 h-20 mb-3 border-4 border-primary/20">
                <AvatarImage src={selectedUser.avatar_url} />
                <AvatarFallback className="bg-primary/10 text-primary text-xl font-semibold">
                  {getInitials(selectedUser.first_name, selectedUser.last_name)}
                </AvatarFallback>
              </Avatar>
              <p className="font-semibold text-lg">
                {selectedUser.first_name} {selectedUser.last_name}
              </p>

              {/* PIN Display */}
              <div className="flex gap-3 my-6">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className={cn(
                      "w-12 h-14 rounded-lg border-2 flex items-center justify-center text-2xl font-bold transition-all",
                      pin.length > i 
                        ? "border-primary bg-primary/5 text-primary" 
                        : "border-slate-200 bg-slate-50"
                    )}
                  >
                    {pin.length > i ? '•' : ''}
                  </div>
                ))}
              </div>

              {/* Numeric Keypad */}
              <div className="grid grid-cols-3 gap-2 w-full max-w-xs">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                  <button
                    key={digit}
                    onClick={() => handlePinInput(String(digit))}
                    disabled={loginLoading}
                    className="h-14 rounded-xl bg-slate-100 hover:bg-slate-200 active:bg-slate-300 font-semibold text-xl text-slate-800 transition-colors disabled:opacity-50"
                    data-testid={`pin-key-${digit}`}
                  >
                    {digit}
                  </button>
                ))}
                <div /> {/* Empty space */}
                <button
                  onClick={() => handlePinInput('0')}
                  disabled={loginLoading}
                  className="h-14 rounded-xl bg-slate-100 hover:bg-slate-200 active:bg-slate-300 font-semibold text-xl text-slate-800 transition-colors disabled:opacity-50"
                  data-testid="pin-key-0"
                >
                  0
                </button>
                <button
                  onClick={handlePinDelete}
                  disabled={loginLoading || pin.length === 0}
                  className="h-14 rounded-xl bg-slate-100 hover:bg-red-100 active:bg-red-200 text-slate-600 hover:text-red-600 transition-colors flex items-center justify-center disabled:opacity-50"
                  data-testid="pin-key-delete"
                >
                  <Delete className="w-6 h-6" />
                </button>
              </div>

              {loginLoading && (
                <div className="mt-4 flex items-center gap-2 text-primary">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Verificando...</span>
                </div>
              )}

              {/* Keyboard hint */}
              {!loginLoading && (
                <p className="mt-4 text-xs text-slate-400 text-center">
                  También puedes usar el teclado de tu computadora
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
